/**
 * Canonical customer-console addresses.
 *
 * User-facing navigation and drill builders import this table. API model names
 * (prompt, evaluation, persona) intentionally remain unchanged; URL vocabulary
 * follows the product model users see.
 */
const segment = (value: string): string => encodeURIComponent(value);

export const consoleRoutes = {
  overview: '/overview',
  visibilityRanking: '/overview/visibility-ranking',
  setup: '/onboarding',

  knowledge: {
    root: '/facts',
    profile: '/facts/profile',
    authority: '/facts/authority',
    factLedger: '/facts/fact-ledger',
    searchDemand: '/facts/search-demand',
    searchDemandTopic: (topicId: string) => `/facts/search-demand/topics/${segment(topicId)}`,
    section: (section: string) => {
      if (section === 'profile') return '/facts/profile';
      if (section === 'authority') return '/facts/authority';
      if (section === 'facts') return '/facts/fact-ledger';
      if (section === 'search-demand') return '/facts/search-demand';
      if (section === 'discoveries') return '/facts/discoveries';
      return `/facts/fact-ledger/${segment(section)}`;
    },
    discoveries: '/facts/discoveries',
    product: (productId: string) => `/facts/fact-ledger/products/${segment(productId)}`,
    capability: (capabilityId: string) => `/facts/fact-ledger/capabilities/${segment(capabilityId)}`,
    capabilityByName: (name: string) => `/facts/fact-ledger/capabilities/by-name/${segment(name)}`,
    decisionCriterion: (criterionId: string) => `/facts/fact-ledger/decision-criteria/${segment(criterionId)}`,
    decisionCriterionByName: (name: string) => `/facts/fact-ledger/decision-criteria/by-name/${segment(name)}`,
    buyingRole: (roleId: string) => `/facts/fact-ledger/buyer-roles/${segment(roleId)}`,
    competitor: (competitorId: string) => `/facts/fact-ledger/competitors/${segment(competitorId)}`,
    competitorProductPrompts: (competitorId: string, offeringId: string) =>
      `/facts/fact-ledger/competitors/${segment(competitorId)}/products/${segment(offeringId)}/prompts`,
    theme: (themeId: string) => `/facts/fact-ledger/themes/${segment(themeId)}`,
    journey: (journeyId: string) => `/facts/fact-ledger/journeys/${segment(journeyId)}`,
    reviewCandidate: (candidateId: string) => `/facts/discoveries/${segment(candidateId)}`,
    fact: (kind: string, factId: string) => `/facts/fact-ledger/facts/${segment(kind)}/${segment(factId)}`,
    factProposal: (proposalId: string) => `/facts/discoveries/fact-proposals/${segment(proposalId)}`,
  },

  demand: {
    root: '/prompts',
    topics: '/prompts',
    questions: '/prompts?view=questions',
    question: (questionId: string) => `/prompts/questions/${segment(questionId)}`,
    topic: (topicId: string) => `/prompts/topics/${segment(topicId)}`,
    keyword: (keyword: string) => `/prompts/topics/keywords/${segment(keyword)}`,
  },

  benchmarks: {
    results: '/benchmarks',
    runs: '/benchmarks?view=runs',
    reportEdit: (reportId: string) => `/benchmarks/reports/${segment(reportId)}/edit`,
    period: (periodId: string) => `/benchmarks/periods/${segment(periodId)}`,
    periodHeadToHead: (periodId: string) => `/benchmarks/periods/${segment(periodId)}?tab=head-to-head`,
    periodSourceChanges: (periodId: string, evaluationId: string, kind: 'competitive' | 'visibility') =>
      `/benchmarks/periods/${segment(periodId)}/changes/${segment(evaluationId)}/${kind}/sources`,
    periodTransitionChanges: (periodId: string, kind: 'competitive' | 'visibility', from: string, to: string) =>
      `/benchmarks/periods/${segment(periodId)}/changes/matrix/${kind}/${segment(from)}/${segment(to)}`,
    periodOwnedPage: (periodId: string, urlHash: string) =>
      `/benchmarks/periods/${segment(periodId)}/pages/${segment(urlHash)}`,
    answer: (evaluationId: string) => `/benchmarks/answers/${segment(evaluationId)}`,
    coverage: (level: string) => `/benchmarks/coverage/${segment(level)}`,
  },

  data: {
    root: '/intelligence',
    /** Query Fan Out — the list, and one `Topic x content need` group's drill-down.
     *  `groupKey` is the server's own `{topic_id|none}:{content_need}`. */
    queryFanOut: '/intelligence/query-fan-out',
    queryFanOutGroup: (groupKey: string) => `/intelligence/query-fan-out/${segment(groupKey)}`,
    sources: '/intelligence/sources',
    source: (urlHash: string) => `/intelligence/sources/${segment(urlHash)}`,
    domain: (domain: string) => `/intelligence/sources/domains/${segment(domain)}`,
    answerClaims: '/intelligence/claims/answers',
    answerClaimTopic: '/intelligence/claims/answers/topics',
    pageClaims: '/intelligence/claims/pages',
    vocabulary: '/intelligence/vocabulary',
    vocabularyPages: '/intelligence/vocabulary/pages',
    themes: '/intelligence/themes',
    issues: '/intelligence/issues',
    issue: (issueId: string) => `/intelligence/issues/${segment(issueId)}`,
  },

  publish: {
    root: '/content',
    playbooks: '/content/playbooks',
    playbook: (playbookId: string) => `/content/playbooks/${segment(playbookId)}`,
    coverage: '/content/coverage',
    coveragePages: '/content/coverage/pages',
    ledger: '/content/agent-ledger',
    pages: '/content/pages',
    page: (urlHash: string) => `/content/pages/${segment(urlHash)}`,
    interfaces: '/content/interfaces',
    access: '/content/access',
    delivery: '/content/delivery',
    updates: '/content/updates',
    policy: '/content/updates/policy',
    cycle: (cycleId: string) => `/content/updates/cycles/${segment(cycleId)}`,
    healthCheck: (domain: string, checkId: string) => `/content/access/checks/${segment(domain)}/${segment(checkId)}`,
  },

  traffic: {
    root: '/traffic',
    agents: '/traffic/agents',
    tools: '/traffic/tools',
    referrals: '/traffic/referrals',
  },

  settings: {
    root: '/settings',
    workspace: '/settings/workspace',
    team: '/settings/team',
    integrations: '/settings/integrations',
    integration: (provider: string) => `/settings/integrations/${segment(provider)}`,
    mcp: '/settings/mcp',
    api: '/settings/api',
    profile: '/settings/profile',
  },
} as const;

export const CUSTOMER_NAV_PREFIXES = [
  consoleRoutes.overview,
  consoleRoutes.knowledge.root,
  consoleRoutes.demand.root,
  consoleRoutes.benchmarks.results,
  consoleRoutes.data.root,
  consoleRoutes.publish.root,
  consoleRoutes.traffic.root,
  consoleRoutes.settings.root,
] as const;
